import React from 'react';
import { useDataFetching } from '../../hooks/useDataFetching';
import LoadingState from '../shared/LoadingState';
import ErrorState from '../shared/ErrorState';
import StatCard from '../shared/StatCard';
import { formatRelativeTime, formatNumber } from '../../utils/formatters';
import { Plus, Filter } from 'lucide-react';

const NoiseFilters = () => {
  const { data: filters, loading, error, refetch } = useDataFetching(
    async () => {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/noise-filters`);
      if (!response.ok) throw new Error('Failed to fetch noise filters');
      return response.json();
    },
    []
  );

  if (loading) return <LoadingState message="Loading noise filters..." />;
  if (error) return <ErrorState message={error} onRetry={refetch} />;

  const activeFilters = filters?.filter(f => f.status === 'active').length || 0;
  const totalFiltered = filters?.reduce((sum, f) => sum + f.events_filtered, 0) || 0;

  const getStatusColor = (status) => {
    return status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Noise Filters</h1>
          <p className="mt-2 text-gray-600">
            Configure filters to reduce noise and focus on important events
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Filters"
            value={filters?.length || 0}
            subtitle="Configured"
          />
          <StatCard
            title="Active Filters"
            value={activeFilters}
            subtitle="Currently filtering"
          />
          <StatCard
            title="Events Filtered"
            value={formatNumber(totalFiltered)}
            subtitle="Total suppressed"
          />
          <StatCard
            title="Noise Reduction"
            value="67%"
            subtitle="Overall effectiveness"
          />
        </div>

        {/* Action Button */}
        <div className="mb-6 flex justify-end">
          <button
            data-testid="new-filter-btn"
            className="inline-flex items-center px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            <Plus className="h-5 w-5 mr-2" />
            New Noise Filter
          </button>
        </div>

        {/* Filters Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Active Noise Filters</h2>
            <p className="text-sm text-gray-600 mt-1">Suppress repetitive or low-priority events</p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Filter Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pattern
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Events Filtered
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filters && filters.map((filter) => (
                  <tr key={filter.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <Filter className="h-5 w-5 text-blue-500 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{filter.name}</div>
                          <div className="text-sm text-gray-500">{filter.description}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded block max-w-xs">
                        {filter.pattern}
                      </code>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(filter.status)}`}>
                        {filter.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatNumber(filter.events_filtered)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatRelativeTime(filter.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button className="text-blue-600 hover:text-blue-900 mr-3">
                        Edit
                      </button>
                      <button className="text-red-600 hover:text-red-900">
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NoiseFilters;